"use client";

import React from "react";
import { useSnapshot } from "valtio";

import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/dialog/dialog/dialog";
import { Switch } from "@/components/switch/switch";
import { Button } from "@/components/button/button/button";
import { renderRichTextWithMutedStrikethrough } from "@/lib/richText";
import type { Blocks } from "@/types/cms";
import {
  privacyChoicesStore,
  setPrivacyChoicesOpen,
} from "@/stores/privacyChoicesStore";
import { writeAdOptOut, writeAnalyticsOptOut } from "@/lib/privacyCookie";

import styles from "./dialogPrivacyChoices.module.css";

export interface DialogPrivacyChoicesData {
  title?: string;
  body?: Blocks;
  necessaryLabel?: string;
  necessaryDescription?: string;
  necessaryRequiredLabel?: string;
  analyticsLabel?: string;
  analyticsDescription?: string;
  marketingLabel?: string;
  marketingDescription?: string;
  saveButtonLabel?: string;
  optOutExplanation?: Blocks;
  optInExplanation?: Blocks;
}

export interface DialogPrivacyChoicesProps {
  data: DialogPrivacyChoicesData;
  initiallyAnalyticsOptedOut: boolean;
  initiallyMarketingOptedOut: boolean;
  gpcApplied: boolean;
  /**
   * Whether the Marketing category row renders in the manage view. The
   * provider passes `false` while the Meta Pixel is unconfigured
   * (`FB_PIXEL_ID` unset) so users aren't shown a toggle for a channel
   * that doesn't exist; it flips back on automatically the day marketing
   * launches. Accept/Reject all still write the `as_ad_optout` cookie
   * either way, so consent is already recorded when the channel goes live.
   */
  showMarketing?: boolean;
}

type AnalyticsPostHog = {
  opt_out_capturing?: () => void;
  opt_in_capturing?: () => void;
};

const DEFAULTS = {
  necessaryLabel: "Necessary",
  necessaryDescription: "Enables security and basic functionality.",
  necessaryRequiredLabel: "Required",
  analyticsLabel: "Analytics",
  analyticsDescription: "Enables tracking of site performance.",
  marketingLabel: "Marketing",
  marketingDescription: "Enables ads personalization and tracking.",
  saveButtonLabel: "Save Preferences",
  acceptAllLabel: "Accept all cookies",
  rejectAllLabel: "Reject all cookies",
  customizeLabel: "Customize cookie settings",
} as const;

export function DialogPrivacyChoices({
  data,
  initiallyAnalyticsOptedOut,
  initiallyMarketingOptedOut,
  gpcApplied,
  showMarketing = true,
}: DialogPrivacyChoicesProps) {
  const { isModalOpen } = useSnapshot(privacyChoicesStore);
  // Two states: `default` (first-run accept/reject/customize) shown on
  // every open; `manage` (the per-category switches + Save) reached via
  // "Customize cookie settings". Resets to `default` each time the
  // dialog opens.
  const [view, setView] = React.useState<"default" | "manage">("default");
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset-on-open sync from the external privacyChoicesStore
    if (isModalOpen) setView("default");
  }, [isModalOpen]);

  const [analyticsOptedOut, setAnalyticsOptedOut] = React.useState(
    initiallyAnalyticsOptedOut,
  );
  const [marketingOptedOut, setMarketingOptedOut] = React.useState(
    initiallyMarketingOptedOut,
  );

  // Shared persist + apply + close path. Writes the opt-out cookies,
  // drives PostHog opt-in/out inline so the change takes effect on the
  // current tab (the cookie persists across navigations; the
  // PostHogProvider startup check rebinds on next load), then closes.
  // The Facebook Pixel server gate re-reads the cookie per-request, so
  // the next navigation picks up the new Marketing state. We deliberately
  // do NOT reload — preserves scroll position.
  const applyAndClose = async (analyticsOut: boolean, marketingOut: boolean) => {
    writeAnalyticsOptOut(analyticsOut);
    writeAdOptOut(marketingOut);

    if (typeof window !== "undefined") {
      try {
        const mod = await import("posthog-js");
        const posthog = mod.default as AnalyticsPostHog;
        if (analyticsOut) {
          posthog.opt_out_capturing?.();
        } else {
          posthog.opt_in_capturing?.();
        }
      } catch {
        // posthog-js may not be installed locally / in this build; the
        // cookie + provider-startup check still covers the next session.
      }
    }

    setPrivacyChoicesOpen(false);
  };

  const handleSave = () => applyAndClose(analyticsOptedOut, marketingOptedOut);

  // Default-state shortcuts — Necessary stays locked-on regardless; only
  // Analytics + Marketing are persisted as opt-outs.
  const handleAcceptAll = () => {
    setAnalyticsOptedOut(false);
    setMarketingOptedOut(false);
    return applyAndClose(false, false);
  };
  const handleRejectAll = () => {
    setAnalyticsOptedOut(true);
    setMarketingOptedOut(true);
    return applyAndClose(true, true);
  };

  // The switch's `checked` represents "consent granted" — opposite of
  // the `*OptedOut` state we persist. Helpers below keep the negation
  // confined to one place per category.
  const onAnalyticsToggle = (consent: boolean) =>
    setAnalyticsOptedOut(!consent);
  const onMarketingToggle = (consent: boolean) =>
    setMarketingOptedOut(!consent);

  return (
    <Dialog open={isModalOpen} onOpenChange={setPrivacyChoicesOpen}>
      <DialogContent
        size="md"
        align="center"
        className={styles.dialogContent}
        title={data.title ?? "Your Privacy Choices"}
        titlePosition="center"
        showIconLeft={view === "manage"}
        onIconLeftClick={() => setView("default")}
      >
        <div
          className={styles.body}
          onClick={(e) => {
            // Inline links in the rich-text body (e.g. the "privacy policy
            // here." link) navigate client-side, which would otherwise leave
            // this dialog open on top of the destination page. Close it on any
            // anchor click. The `closest("a")` guard means switch toggles and
            // other body interactions are unaffected.
            if ((e.target as HTMLElement).closest("a")) {
              setPrivacyChoicesOpen(false);
            }
          }}
        >
          {Array.isArray(data.body) && data.body.length > 0 ? (
            <div className={styles.summary}>
              {renderRichTextWithMutedStrikethrough(data.body, {
                paragraphClass: "body-sm",
                mutedParagraphs: false,
              })}
            </div>
          ) : null}

          {/* Category cards (Necessary/Analytics/Marketing) only show in
              the manage view. The default (first-run) state is just the
              summary + GPC status line + the accept/reject/customize
              buttons. Necessary is locked-on with the "Required" badge;
              its Switch is disabled + forced checked so users can't
              toggle session/auth/preference cookies off. */}
          {view === "manage" ? (
            <>
              <CategoryRow
                label={data.necessaryLabel ?? DEFAULTS.necessaryLabel}
                description={
                  data.necessaryDescription ?? DEFAULTS.necessaryDescription
                }
                stateLabel={
                  data.necessaryRequiredLabel ??
                  DEFAULTS.necessaryRequiredLabel
                }
                switchChecked
                switchDisabled
              />

              <CategoryRow
                label={data.analyticsLabel ?? DEFAULTS.analyticsLabel}
                description={
                  data.analyticsDescription ?? DEFAULTS.analyticsDescription
                }
                stateLabel={analyticsOptedOut ? "Off" : "On"}
                switchChecked={!analyticsOptedOut}
                onSwitchChange={onAnalyticsToggle}
              />

              {showMarketing ? (
                <CategoryRow
                  label={data.marketingLabel ?? DEFAULTS.marketingLabel}
                  description={
                    data.marketingDescription ?? DEFAULTS.marketingDescription
                  }
                  stateLabel={marketingOptedOut ? "Off" : "On"}
                  switchChecked={!marketingOptedOut}
                  onSwitchChange={onMarketingToggle}
                />
              ) : null}
            </>
          ) : null}

          {/* Override = current toggle state diverges from GPC's
              expected default. With GPC the default is both opted out;
              without GPC the default is both opted in. Uses local
              state (`*OptedOut`) so the label updates live as the
              user flips toggles, before Save is clicked. */}
          {(() => {
            // Only the categories the user can actually see feed the
            // override detection — a hidden Marketing row must not surface
            // a ", User Overridden" the user can't trace to any toggle.
            const overridden = gpcApplied
              ? !analyticsOptedOut || (showMarketing && !marketingOptedOut)
              : analyticsOptedOut || (showMarketing && marketingOptedOut);
            const base = gpcApplied
              ? "Opt-Out Signal Honored"
              : "No Opt-Out Signal Detected";
            return (
              <div className={styles.gpcRow}>
                <div
                  className={`title-xs ${styles.gpcStatus}${
                    view === "default" ? ` ${styles.gpcStatusCentered}` : ""
                  }`}
                >
                  {view === "default" ? (
                    <span className={styles.gpcDot} aria-hidden="true" />
                  ) : null}
                  {base}
                  {overridden ? ", User Overridden" : ""}
                </div>
                {(() => {
                  // Opt-out subtext is hidden in the default state — only
                  // the status line above shows there.
                  if (view !== "manage") return null;
                  const explanation = gpcApplied
                    ? data.optOutExplanation
                    : data.optInExplanation;
                  if (!Array.isArray(explanation) || explanation.length === 0) {
                    return null;
                  }
                  return (
                    <div className={styles.gpcNote}>
                      {renderRichTextWithMutedStrikethrough(explanation, {
                        paragraphClass: "body-sm",
                        mutedParagraphs: true,
                      })}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>

        <DialogFooter>
          {view === "manage" ? (
            <Button variant="primary" size="lg" onClick={handleSave}>
              {data.saveButtonLabel ?? DEFAULTS.saveButtonLabel}
            </Button>
          ) : (
            <div className={styles.defaultActions}>
              <Button
                variant="default"
                size="lg"
                className={styles.fullWidthButton}
                onClick={() => setView("manage")}
              >
                {DEFAULTS.customizeLabel}
              </Button>
              <div className={styles.defaultActionsRow}>
                <Button
                  variant="default"
                  size="lg"
                  className={styles.rowButton}
                  onClick={handleRejectAll}
                >
                  {DEFAULTS.rejectAllLabel}
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  className={styles.rowButton}
                  onClick={handleAcceptAll}
                >
                  {DEFAULTS.acceptAllLabel}
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryRowProps {
  label: string;
  description: string;
  stateLabel: string;
  switchChecked: boolean;
  switchDisabled?: boolean;
  onSwitchChange?: (consent: boolean) => void;
}

function CategoryRow({
  label,
  description,
  stateLabel,
  switchChecked,
  switchDisabled,
  onSwitchChange,
}: CategoryRowProps) {
  return (
    <div className={styles.categoryRow}>
      <div className={styles.categoryText}>
        <div className={`title-xs ${styles.categoryLabel}`}>{label}</div>
        <div className={`body-sm ${styles.categoryDescription}`}>
          {description}
        </div>
      </div>
      <div className={styles.categoryControl}>
        <span className={styles.categoryStateLabel}>{stateLabel}</span>
        <Switch
          checked={switchChecked}
          disabled={switchDisabled}
          onCheckedChange={onSwitchChange}
          aria-label={label}
        />
      </div>
    </div>
  );
}

